/** Part I — Arrival: the manual itself, the world, your account, and finding your way around the screen. */
import type { HelpPart } from './types.js';

export const PART_ARRIVAL: HelpPart = {
  numeral: 'I',
  title: 'Arrival',
  chapters: [
    {
      id: 'welcome',
      title: 'Welcome to Cosmos',
      blurb: 'What this world is, who lives in it, and what you start with.',
      pages: [
        {
          id: 'welcome/manual',
          title: 'Using this manual',
          terms: ['help', 'book', 'search', 'index', 'bookmark'],
          body: `<p>This book covers everything you can do in Cosmos. Open it any time with <kbd>H</kbd>, the
          <b>Help</b> button on the menu bar, or the <b>?</b> button in the corner of the screen.</p>
          <p>Three ways in, depending on what you know:</p>
          <ul>
          <li><b>Search</b> — type into the box at the top. Every page is searched, titles first. Press
          <kbd>Enter</kbd> to open the best match, or <kbd>↓</kbd> to walk the results.</li>
          <li><b>Contents</b> — the chapters in reading order, grouped into six parts. The spine on the left always
          shows where you are; the open chapter lists its pages.</li>
          <li><b>Index</b> — every key term in the book, A to Z, with the pages that explain it.</li>
          </ul>
          <p>Turn pages with <kbd>←</kbd> and <kbd>→</kbd> (or the page names at the foot of each page).
          <kbd>Home</kbd> returns to the contents, <kbd>/</kbd> jumps to the search box, and <kbd>Backspace</kbd>
          goes back to the page you came from. <b>Mark this page</b> drops a ribbon on it; your marks are listed
          under <b>Marks</b>. The book reopens where you left it.</p>
          <p>Keys in this book are your <i>current</i> bindings: rebind a key in Options and every page follows.</p>`,
        },
        {
          id: 'welcome/cosmos',
          title: 'What Cosmos is',
          terms: ['pre-alpha', 'server authoritative', 'persistent'],
          body: `<p>Cosmos is a persistent, shared world: a settled far planet three generations after the first
          landing, two peoples, hundreds of living towns, open wilds, and a solar system overhead you can fly to.
          Everyone plays in the same world at the same time.</p>
          <p>The world is <b>server-authoritative</b>. The server decides where you are, what you own, what money
          you have and what you may do; your game draws the world and predicts your movement so it feels
          immediate, then agrees with the server. Nothing you own lives only on your computer.</p>
          <p>This is a <b>pre-alpha build</b>. It is not the final game: expect placeholder art, rough edges,
          bugs and occasional resets.</p>`,
        },
        {
          id: 'welcome/worlds',
          title: 'Kinds of world',
          terms: ['personal world', 'commons', 'arcade', 'group world'],
          body: `<ul>
          <li><b>The Commons</b> — the one shared world everyone starts in: the continent, its towns and wilds,
          and the solar system above it. It belongs to the platform; you live, trade and fight here but build only
          where the rules allow (your own deeds, your guild's land, your city).</li>
          <li><b>Your personal world</b> — a 64 × 64 m world you own, made when your account was created. You hold
          every right there. Swap between it and the Commons with {{key:map}}.</li>
          <li><b>More worlds of your own</b> — create as many as you like from <b>My Worlds</b> (<kbd>F8</kbd>), each
          with its own size, visibility and guest list.</li>
          <li><b>Group worlds</b> — owned by a group and governed by its charter.</li>
          <li><b>Arcade worlds</b> — matchmade competitive instances with leaderboards.</li>
          <li><b>The Merchant Hive</b> — a commerce planet of shops; <kbd>F9</kbd> takes you there and back.</li>
          </ul>`,
          see: ['homes/personal', 'welcome/hive'],
        },
        {
          id: 'welcome/commons',
          title: 'The Commons — the land',
          terms: ['biomes', 'settlements', 'capital', 'grand circuit', 'continent'],
          body: `<p>The Commons is a single continent more than a thousand kilometres across. Its ground is made of
          six biomes — <b>regolith plains</b>, <b>moss wetland</b>, <b>rocky highlands</b>, the <b>crystal
          forest</b>, the <b>dune desert</b> and the <b>fungal basin</b> — with rivers, lakes, waterfalls, caves,
          terraced hills, deep woods and a sea around it.</p>
          <p>About three hundred settlements stand on it: <b>cities</b>, <b>towns</b> and <b>hamlets</b>, each on a
          level plateau with its own streets, markets, homes and people. Two walled <b>capitals</b> — one for each
          people, thirty wards apiece — anchor the map, and the <b>Grand Circuit</b>, a 31 km hovercraft race
          track, loops between them.</p>
          <p>Between the towns are the <b>wilds</b>: open country with herds, birds, insects and dangerous
          creatures. Roads link the towns; the World Atlas (<kbd>N</kbd>) shows them all.</p>`,
          see: ['maps/atlas', 'weather/wildlife'],
        },
        {
          id: 'welcome/peoples',
          title: 'The two peoples',
          terms: ['aurelian', 'borean', 'civilisation', 'concord', 'compact'],
          body: `<p>The settlers split long ago into two civilisations. The <b>Aurelians</b> (gold is their colour;
          their names ring: Solvarum, Corvessa, Thalaris) and the <b>Boreans</b> (ice-blue; their names are counted:
          Skjolmark, Fenholt, Brynstad). In space their navies are the <b>Aurelian Concord</b> and the
          <b>Borean Compact</b>.</p>
          <p>Every town flies one flag, and towns change hands in wars. You can live and trade with both; in space
          you may swear to one navy, and the other will then mark you as an enemy.</p>`,
          see: ['politics/civs', 'space/duty'],
        },
        {
          id: 'welcome/starter',
          title: 'Your starter kit',
          terms: ['knife', 'pistol', 'field tool', 'dust skiff', 'lancer', 'first join'],
          body: `<p>The first time you join you are given a starter kit:</p>
          <ul>
          <li>a <b>vibro knife</b> and a <b>scrap pistol</b> — equip them from the inventory; {{key:attack}} or a
          click (with the mouse captured) attacks, {{key:weaponNext}} cycles weapons;</li>
          <li>a <b>field tool</b> — equipped, every hand-sample of a resource yields more;</li>
          <li>a packed <b>Field Extractor Rig</b> deed — place it on a resource hotspot and it mines while you are
          away;</li>
          <li>a <b>Dust Skiff</b> hovercraft — ride it from the inventory, or <kbd>X</kbd> in the open;</li>
          <li>a <b>Lancer</b>, a starter ship in your hangar — fly it from a city spaceport or call it to you in the
          field.</li>
          </ul>
          <p>Press <kbd>F1</kbd> for your inventory. Lose every ship and a fresh Lancer is issued.</p>`,
          see: ['inventory/inventory', 'vehicles/hovercraft', 'inventory/rigs'],
        },
        {
          id: 'welcome/hive',
          title: 'The Merchant Hive',
          terms: ['hive', 'f9', 'shops'],
          body: `<p>A commerce planet in the Commons' solar system: flat brick-and-steel ground and one great city of
          shops. Press <kbd>F9</kbd> (or <b>Hive</b> on the menu bar) to teleport there; your exact spot is remembered
          and the <b>↩ Return</b> chip, or <kbd>F9</kbd> again, puts you back. You can also fly: the Hive hangs on the
          space chart like any world.</p>`,
        },
        {
          id: 'welcome/new',
          title: 'What is new',
          terms: ['new', 'changes', 'update', 'patch notes'],
          body: `<p>Recent additions, newest first. Each links to its page.</p>
          <p><b>October 2026</b></p>
          <ul>
          <li>This <b>field manual</b> — every system, searchable, with an index and bookmarks.</li>
          <li>Water answers more: shots and cannon fire <a data-page="water/shots">splash and hiss</a> where they land,
          ships <a data-page="water/craft">blow the water flat</a> when flying low and skim rather than dive, and you
          come out of a swim <a data-page="water/wet">dripping and leaving wet footprints</a>.</li>
          <li><a data-page="moving/autorun">Auto-run</a> ({{key:autoRun}}).</li>
          <li>A <a data-page="weather/weather">weather forecast</a> in the Town window.</li>
          <li>An <a data-page="combat/telegraphs">arc at the screen's edge</a> that points to a blow wound up at you.</li>
          <li>Live events show on both <a data-page="maps/local">maps</a>; a click sets a waypoint to them.</li>
          <li>Chat <a data-page="talk/translate">translation</a> has its own settings under Options → General → Social.</li>
          <li>A device whose sign-in was ended elsewhere is <a data-page="account/password">sent to Sign in</a> before it
          can trade.</li>
          </ul>`,
          see: ['welcome/manual'],
        },
      ],
    },
    {
      id: 'account',
      title: 'Your account',
      blurb: 'Signing in, securing an older traveler, recovery codes, and bringing friends.',
      pages: [
        {
          id: 'account/signin',
          title: 'Signing in and creating an account',
          terms: ['login', 'register', 'password', 'username', 'create account'],
          body: `<p>Cosmos asks you to sign in before it connects. The start screen offers <b>Sign in</b>,
          <b>Create account</b> and <b>Forgot password</b>.</p>
          <ul>
          <li><b>Username</b> — 3 to 20 letters, digits or underscores. It is permanent and nobody else can take it,
          whatever its case. (Your <b>display name</b> is separate and can change once a week.)</li>
          <li><b>Password</b> — 8 to 128 characters, anything you like. Very common passwords, and your own
          username, are refused.</li>
          </ul>
          <p>Creating an account shows your <b>recovery codes</b> once. Save them before you tick
          “I saved these” — they are the only way back in if you forget the password.</p>
          <p>Once signed in you stay signed in on that browser for weeks; the session renews itself while you
          play.</p>`,
          see: ['account/codes', 'account/password'],
        },
        {
          id: 'account/claim',
          title: 'Securing an older traveler',
          terms: ['secure your account', 'claim', 'set a password', 'banner', 'unclaimed'],
          body: `<p>Travelers made before sign-in existed have no password yet. Until you set one, a
          <b>Secure your account</b> banner appears at the top of the screen. <b>Set a password</b> (on the banner,
          or Options → General → Account) gives your existing traveler a username login and recovery codes — same
          character, same wallet, same things.</p>
          <p><b>Later</b> hides the banner until tomorrow. Without a password, the traveler lives only in this
          browser: clearing it, or signing out, loses it. That is why <b>Sign out</b> on an unsecured traveler asks
          you first and offers to set the password instead.</p>`,
          see: ['account/signin'],
        },
        {
          id: 'account/codes',
          title: 'Recovery codes',
          terms: ['forgot password', 'recover', 'regenerate', 'codes left'],
          body: `<p>You get <b>eight recovery codes</b>, ten characters each, shown once. Each works once.
          <b>Forgot password</b> on the start screen takes your username, one code and a new password.</p>
          <p>Options → General → Account shows how many codes you have left and warns when you are down to two.
          <b>Make new codes</b> replaces the whole set (the old ones stop working). A valid code still works while
          your account is locked after too many wrong passwords.</p>`,
        },
        {
          id: 'account/password',
          title: 'Changing your password and signing out',
          terms: ['sign out', 'sign out everywhere', 'change password', 'locked', 'lockout'],
          body: `<p>Options → General → <b>Account</b>:</p>
          <ul>
          <li><b>Change password</b> — needs your current password; the game reconnects afterwards and every
          other session of yours is signed out.</li>
          <li><b>Sign out</b> — this browser only.</li>
          <li><b>Sign out everywhere</b> — ends every session you have, on every device.</li>
          </ul>
          <p>A device whose session was ended this way is taken to <b>Sign in</b> the next time it tries to trade, buy, sell
          or move money — it can no longer act for you. Signing in on a second device does not sign the first one out.</p>
          <p>Too many wrong passwords from one place lock that place out of your account for a while (a minute at
          first, up to an hour). A recovery code always works.</p>`,
        },
        {
          id: 'account/invite',
          title: 'Inviting a friend',
          terms: ['invite link', 'discord', 'join'],
          body: `<p><b>Invite</b> on the menu bar makes a link. A friend who opens it signs in and arrives standing
          beside you — wherever you are in the Commons. A link lasts 48 hours and works for ten people; you can hold
          five live links at a time.</p>
          <p>Inside a Discord Activity the same button opens Discord's own invite.</p>`,
        },
      ],
    },
    {
      id: 'around',
      title: 'Finding your way around',
      blurb: 'The menu bar, the keys, windows, and what Escape does.',
      pages: [
        {
          id: 'around/menubar',
          title: 'The menu bar',
          terms: ['buttons', 'panels', 'menu'],
          body: `<p>The bar along the bottom of the screen opens every panel in the game; a key is never required.
          Hover a button for its name and key. The <b>≡</b> chip folds the bar away.</p>
          <table>
          <tr><td><b>Help</b> · <b>Options</b></td><td>This book; graphics, sound, controls and account (<kbd>Esc</kbd>)</td></tr>
          <tr><td><b>Local</b> · <b>Map</b></td><td>The local map (<kbd>M</kbd>) and the World Atlas (<kbd>N</kbd>)</td></tr>
          <tr><td><b>Worlds</b> · <b>Hive</b> · <b>Swap</b></td><td>My Worlds (<kbd>F8</kbd>), the Merchant Hive (<kbd>F9</kbd>), Commons ↔ personal world ({{key:map}})</td></tr>
          <tr><td><b>Passport</b></td><td>Your avatar, profile, reputation, events, memory book (<kbd>P</kbd>)</td></tr>
          <tr><td><b>Compass</b> · <b>Orbits</b></td><td>Your Want and Fear (<kbd>C</kbd>); the people travelling with you (<kbd>O</kbd>)</td></tr>
          <tr><td><b>Social</b></td><td>Party, friends, messages, trade, blocking (<kbd>G</kbd>)</td></tr>
          <tr><td><b>Guilds</b></td><td>Your guild, its court, land and the directory (<kbd>V</kbd>)</td></tr>
          <tr><td><b>Assembly</b> · <b>City</b> · <b>Town</b></td><td>The federal docket; the city console; town life (festivals, races, posts, bounties, contracts)</td></tr>
          <tr><td><b>Invite</b> · <b>Voice</b> · <b>Bank</b></td><td>Invite links; voice rooms; your bank</td></tr>
          <tr><td><b>Edit</b></td><td>Edit Mode, where you may build (<kbd>F2</kbd>)</td></tr>
          <tr><td><b>Inventory</b> · <b>Craft</b> · <b>Recipes</b></td><td>Your things (<kbd>F1</kbd>), the Workbench (<kbd>F6</kbd>), the Crafting Guide (<kbd>F10</kbd>)</td></tr>
          <tr><td><b>Droids</b> · <b>Guide</b></td><td>Your droid datapad; the Field Guide</td></tr>
          <tr><td><b>Logic</b> · <b>Lua IDE</b></td><td>World Logic (<kbd>L</kbd>); the Lua IDE (<kbd>I</kbd>, inside Edit Mode)</td></tr>
          <tr><td><b>Perf</b> · <b>Versonas</b></td><td>Performance readout (<kbd>F3</kbd>); the Versona Lab (<kbd>F4</kbd>)</td></tr>
          </table>
          <p>In space the bar changes to the flight set: charts, nav, hyperdrive, targets, docking, power and fire.</p>`,
          see: ['around/keys', 'space/reach'],
        },
        {
          id: 'around/keys',
          title: 'Keys at a glance',
          terms: ['controls', 'hotkeys', 'keyboard', 'bindings'],
          body: `<p>On foot (all rebindable in Options → Controls):</p>
          <table>
          <tr><td>{{key:moveForward}} {{key:moveLeft}} {{key:moveBack}} {{key:moveRight}}</td><td>Move</td></tr>
          <tr><td>{{key:sprint}}</td><td>Sprint (hold)</td></tr>
          <tr><td>{{key:jump}}</td><td>Jump; climb a ledge you are pushing into; swim up</td></tr>
          <tr><td>{{key:crouch}}</td><td>Crouch (toggle); slide while sprinting; dive while swimming</td></tr>
          <tr><td>{{key:attack}} or click</td><td>Attack (with the mouse captured)</td></tr>
          <tr><td>Right mouse (hold)</td><td>Guard; raising it opens a short parry window</td></tr>
          <tr><td>{{key:weaponNext}}</td><td>Next weapon (with Shift: previous)</td></tr>
          <tr><td>{{key:interact}}</td><td>Use the thing in front of you — doors, terminals, seats, fishing</td></tr>
          <tr><td>{{key:autoRun}}</td><td>Auto-run on / off</td></tr>
          <tr><td>{{key:voicePtt}}</td><td>Push to talk (voice rooms, when set to push-to-talk)</td></tr>
          <tr><td>{{key:map}}</td><td>Swap between the Commons and your personal world</td></tr>
          </table>
          <p>Fixed keys: <kbd>H</kbd> this book · <kbd>Enter</kbd> chat · <kbd>Esc</kbd> close / options ·
          <kbd>F1</kbd> inventory · <kbd>F2</kbd> Edit Mode · <kbd>F3</kbd> performance · <kbd>F6</kbd> Workbench ·
          <kbd>F8</kbd> My Worlds · <kbd>F9</kbd> the Hive · <kbd>F10</kbd> Crafting Guide. Panel keys: <kbd>M</kbd>
          local map (<kbd>Ctrl</kbd>+<kbd>M</kbd> overlay) · <kbd>N</kbd> World Atlas · <kbd>P</kbd> Passport ·
          <kbd>C</kbd> Compass · <kbd>O</kbd> Orbits · <kbd>G</kbd> Social · <kbd>V</kbd> Guilds · <kbd>T</kbd> talk
          to a townsperson · <kbd>K</kbd> a droid's terminal · <kbd>X</kbd> mount a hovercraft · <kbd>R</kbd>
          reload.</p>
          <p>Riding, flying and space have their own pages: see <a data-page="vehicles/hovercraft">Hovercraft</a>,
          <a data-page="vehicles/ship">Your ship in the sky</a> and <a data-page="space/combat">Space combat</a>.</p>`,
          see: ['options/controls', 'options/gamepad'],
        },
        {
          id: 'around/windows',
          title: 'Windows',
          terms: ['drag', 'resize', 'roll up', 'opacity', 'close'],
          body: `<p>Panels open as windows you can arrange. Drag the title bar to move one, drag the corner grip to
          resize, double-click the title (or the — button) to roll it up, the droplet cycles its opacity, and ×
          closes it. Each window remembers where you left it, its size and its opacity.</p>
          <p><kbd>Esc</kbd> closes the window on top. With a controller, <b>B</b> does the same and the d-pad moves
          between buttons; the right stick scrolls.</p>`,
          see: ['around/escape'],
        },
        {
          id: 'around/escape',
          title: 'What Escape does',
          terms: ['esc', 'options menu', 'pointer lock', 'cutscene'],
          body: `<p><kbd>Esc</kbd> always does the first of these that applies:</p>
          <ol>
          <li>While you type in a field, it is the field's (it never opens anything).</li>
          <li>If the Options menu is open, it closes it.</li>
          <li>During a cutscene it skips the scene; on the placement map it leaves the map; with the mouse captured
          it releases the mouse.</li>
          <li>A sign-in form you opened yourself closes.</li>
          <li>The window on top closes.</li>
          <li>Otherwise the Options menu opens.</li>
          </ol>`,
        },
        {
          id: 'around/hud',
          title: 'The screen',
          terms: ['hud', 'health bar', 'wallet', 'pulse', 'chips', 'toast'],
          body: `<ul>
          <li><b>Wallet</b> (top centre) — your credits.</li>
          <li><b>Health and action bar</b> (bottom centre) — health, the weapon in hand and its ammunition or charge,
          and the attack cooldown sweeping over the button.</li>
          <li><b>Pulse</b> — your four needs (social, fun, creativity, rest), shown as gauges.</li>
          <li><b>Chips</b> — small status tags that come and go: auto-run, your service post, a race, a storm,
          “wanted”, a festival you are attending.</li>
          <li><b>Toasts</b> — short messages at the bottom centre that say what just happened or why something was
          refused. They fade after a few seconds.</li>
          <li><b>Prompts</b> — “E — open the door”, “E — cast a line”: what the use key does right here.</li>
          <li><b>The heading strip</b> — under the wallet when you follow a waypoint: which way to turn, and how
          far.</li>
          </ul>
          <p>During a cutscene the screen clears to the picture alone.</p>`,
        },
      ],
    },
  ],
};
